import { State } from 'models';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'hooks';
import {
  fetchSpaces,
  fetchMeters,
  fetchConsumptions,
} from 'store/consumption/actions';
import {
  selectMeter,
  setEndDate,
  setStartDate,
  selectSpace,
  resetState,
  setConsumptionsSortParams,
} from 'store/consumption/action-creators';
import {
  getSortedConsumptions,
  selectConsumptionsSortParams,
} from 'store/consumption/selectors';
import { Meter, SortParamsArg } from 'models';
import { ConsumptionDetails } from 'components';

const extractProps = (state: State) => ({
  spaces: state.consumption.spaces,
  spacesLoading: state.consumption.spacesLoading,
  meters: state.consumption.meters,
  metersLoading: state.consumption.metersLoading,
  consumptions: getSortedConsumptions(state),
  consumptionsLoading: state.consumption.consumptionsLoading,
  consumptionsSortParams: state.consumption.sortParams,
  startDate: state.consumption.startDate,
  endDate: state.consumption.endDate,
  sortParams: selectConsumptionsSortParams(state),
  selectedCoopId: state.settings.current_cooperative,
  selectedSpaceId: state.consumption.selectedSpaceId,
  selectedMeter: state.consumption.selectedMeter,
});

const Consumption = () => {
  const dispatch = useDispatch();
  const {
    selectedCoopId,
    selectedSpaceId,
    selectedMeter,
    startDate,
    endDate,
    ...rest
  } = useSelector(extractProps);
  const prevCoopId = usePrevious(selectedCoopId);

  useEffect(() => {
    if (prevCoopId !== selectedCoopId) {
      dispatch(resetState());
    }
    return () => {
      dispatch(resetState());
    };

    /* eslint-disable-next-line */
  }, [dispatch, selectedCoopId]);

  useEffect(() => {
    if (selectedCoopId && prevCoopId !== selectedCoopId) {
      dispatch(fetchSpaces(selectedCoopId));
    }

    /* eslint-disable-next-line */
  }, [dispatch, selectedCoopId]);

  useEffect(() => {
    if (selectedCoopId && selectedSpaceId && prevCoopId === selectedCoopId) {
      dispatch(fetchMeters(selectedCoopId, selectedSpaceId));
    }

    /* eslint-disable-next-line */
  }, [dispatch, selectedSpaceId]);

  useEffect(() => {
    if (
      selectedCoopId &&
      selectedSpaceId &&
      selectedMeter &&
      prevCoopId === selectedCoopId
    ) {
      dispatch(
        fetchConsumptions(
          startDate,
          endDate,
          selectedCoopId,
          selectedSpaceId,
          selectedMeter.Id
        )
      );
    }

    /* eslint-disable-next-line */
  }, [selectedMeter, startDate, endDate]);

  const handleChangeCurrentMeter = useCallback(
    (meter: Meter) => {
      dispatch(selectMeter(meter));
    },
    [dispatch]
  );

  const handleChangeStartDate = useCallback(
    (date: Date | null) => {
      dispatch(setStartDate(date));
    },
    [dispatch]
  );

  const handleChangeEndDate = useCallback(
    (date: Date | null) => {
      dispatch(setEndDate(date));
    },
    [dispatch]
  );

  const handleChangeSpace = useCallback(
    (id: string) => {
      if (id !== selectedSpaceId) {
        dispatch(selectSpace(id));
      }
    },
    [dispatch, selectedSpaceId]
  );

  const handleChangeSortParams = useCallback(
    (params: SortParamsArg) => {
      dispatch(setConsumptionsSortParams(params));
    },
    [dispatch]
  );

  return (
    <>
      <ConsumptionDetails
        {...rest}
        startDate={startDate}
        endDate={endDate}
        selectedSpaceId={selectedSpaceId}
        selectedMeter={selectedMeter}
        handleChangeCurrentMeter={handleChangeCurrentMeter}
        handleChangeSortParams={handleChangeSortParams}
        handleChangeStartDate={handleChangeStartDate}
        handleChangeEndDate={handleChangeEndDate}
        handleChangeSpace={handleChangeSpace}
      />
    </>
  );
};

export default Consumption;
